// ПвП.6.1: адаптер Colyseus state → engine.Match shape.
//
// BattleScreen.tsx умеет читать только структуру vanilla-движка
// (match.players[i].field[0][2], match.players[i].hero.current.atk и т.д.).
// Сервер шлёт более плоский Schema-формат (line0/line1, плоские atk/armor/hp).
// Здесь мы конвертируем, чтобы BattleScreen работал в PvP без хирургии
// в render-коде.
//
// КРИТИЧНО: на клиенте match.players[0] ВСЕГДА «я», [1] — «враг», даже
// если на сервере мой sessionId находится в state.players[1]. Поэтому
// при `myIdx === 1` мы СВАПАЕМ массив и инвертируем firstPlayerIdx.
//
// Что НЕ синхронизируется через MatchState (не в schema):
//   • pendingPlacements / pendingMods (приватный server-state)
//   • pendingAction в руке (приватный)
// (influenceOffer / influencePlayCounts уже синкаются — ПвП.fix-influence.)
//
// Маркер `_pvp = true` ставим на корень — по нему engine-shim роутит
// действия через room.send.

/* eslint-disable @typescript-eslint/no-explicit-any */

// ПвП.fix-hero-text: lookup данных Героев для подкладывания ability_text
// и archetype (HeroState schema их не несёт — гонять через WS статику
// нет смысла). getHeroes(factionId) объединяет все 5 файлов heroes-*.js.
// @ts-ignore — JS-файл, без d.ts.
import { getHeroes } from '../../../src/data/heroes.js';

export interface ColyseusSnap {
  phase: string;
  currentTurn: number;
  firstPlayerIdx: number;
  matchOver: boolean;
  winnerSessionId: string;
  players: any[];
  recentLog: string[];
  phaseDeadlineMs?: number; // ТАЙ.1: unix-мс или 0 если фаза без таймера
  // ВОЙ.1: Война Корпораций.
  warStakeUsd?: number;
  warMultiplier?: number;
  warSnapPendingBy?: string;
  warSnapDeadlineMs?: number;
  warEndReason?: string;
}

// Конвертирует один Unit из schema-формата в Card-shape движка.
function adaptUnit(u: any): any {
  if (!u || u.instanceId === 0) return null; // sentinel пустой слот → null
  const keywords = u.keywords || [];
  return {
    instanceId: u.instanceId,
    id: u.id,
    name_ru: u.name_ru,
    type: u.type || 'unit',
    rarity: u.rarity || 'common',
    // Базовые статы (immutable из шаблона).
    base: { atk: u.baseAtk, armor: u.baseArmor, hp: u.baseHp },
    // Текущие — с учётом баффов/урона.
    current: {
      atk: u.atk, armor: u.armor, hp: u.hp,
      keywords, silenced: !!u.silenced,
    },
    // Плоские поля для legacy-кода который читает u.atk напрямую.
    atk: u.atk, armor: u.armor, hp: u.hp,
    keywords,
    alive: !!u.alive,
    isAlive: function () { return !!this.alive; },
    // ПвП.fix-methods: engine.getActivatableStealth и др. читают
    // unit.isSleeping(currentTurn). Адаптер строит карту как plain JS,
    // методы Card class теряются — нужно re-добавить.
    // sleeperTurnsLeft синхронизируется сервером после каждого хода
    // (sleeperState?.turnsLeft в engine), так что currentTurn ignore.
    isSleeping: function () { return (this.sleeperTurnsLeft || 0) > 0; },
    isStatModified: function (name: string) {
      return this.current?.[name] !== this.base?.[name];
    },
    silenced: !!u.silenced,
    silenceSources: u.silenced ? new Set(['_pvp_']) : new Set(),
    sleeperState: u.sleeperTurnsLeft > 0 ? 'sleeping' : null,
    sleeperTurnsLeft: u.sleeperTurnsLeft || 0,
    modifications: u.modifications || [],
    line: u.line, slot: u.slot,
    revealed: true,        // в PvP всё что в state — раскрыто
    placedTurn: 0,         // не имеет значения для рендера
    // ПвП.fix-priority: приоритет атаки Глобала (выбор игрока в Ф2).
    // '' = не выставлен (UI fallback'нет на дефолт фракции). Передаём
    // undefined если '', чтобы UI-проверки `if (unit.attackPriority)` работали.
    attackPriority: u.attackPriority || undefined,
    attackPriorityKeyword: undefined,
    // 2d: приоритет лечения хилера (выбор игрока в Ф2).
    healPriority: u.healPriority || undefined,
  };
}

function adaptHandCard(c: any): any {
  if (!c) return null;
  return {
    instanceId: c.instanceId,
    id: c.id,
    name_ru: c.name_ru,
    type: c.type || 'unit',
    rarity: c.rarity || 'common',
    // ПвП.6.3: faction нужен для needsAttackPriorityPicker (Глобал) +
    // других faction-зависимых UI-проверок.
    faction: c.faction || '',
    base: { atk: c.baseAtk, armor: c.baseArmor, hp: c.baseHp },
    current: { atk: c.baseAtk, armor: c.baseArmor, hp: c.baseHp, keywords: [], silenced: false },
    atk: c.baseAtk, armor: c.baseArmor, hp: c.baseHp,
    keywords: [],
    influence_cost: c.influenceCost || 0,
    initiation: 0, // не синкается, картинку 'спит до хода X' не нарисуем
    revealed: false,
    // ПвП.fix-priority: сохранённый выбор приоритета (если игрок уже
    // прошёл picker для этой Глобал-карты в Ф2).
    attackPriority: c.attackPriority || undefined,
    // 2d: сохранённый выбор приоритета лечения (хилеры).
    healPriority: c.healPriority || undefined,
  };
}

function adaptHero(h: any): any {
  if (!h) return null;
  const keywords = h.keywords || [];
  // ПвП.fix-hero-text: ability_text + archetype не приходят через
  // HeroState schema (это статика карты — нет смысла гонять через WS).
  // Lookup'аем из heroes-<faction>.js по id и подкладываем.
  let tpl: any = null;
  try {
    const heroes = getHeroes(h.faction || '');
    tpl = (heroes as any[]).find((x: any) => x.id === h.id) || null;
  } catch { /* fallback пусто */ }
  return {
    id: h.id || '',
    name_ru: h.name_ru || tpl?.name_ru || '',
    faction: h.faction || '',
    type: 'hero',
    archetype: tpl?.archetype || null,
    ability_text: tpl?.ability_text || {},
    ability_short: tpl?.ability_short || null,
    flavor_ru: tpl?.flavor_ru || null,
    base: { atk: h.baseAtk, armor: h.baseArmor, hp: h.baseHp },
    current: {
      atk: h.atk, armor: h.armor, hp: h.hp,
      keywords, silenced: !!h.silenced,
    },
    atk: h.atk, armor: h.armor, hp: h.hp,
    keywords,
    silenced: !!h.silenced,
    silenceSources: h.silenced ? new Set(['_pvp_']) : new Set(),
    heroActiveUsed: !!h.heroActiveUsed,
    heroActiveUsedThisMatch: !!h.heroActiveUsed,
    heroAttackedThisTurn: !!h.heroAttackedThisTurn,
    alive: (h.hp ?? 0) > 0,
    isAlive: function () { return this.alive; },
    // Hero никогда не «спит» в смысле Initiation Cooldown — он на поле
    // с 1-го хода всегда. Метод нужен чтобы engine.getActivatableStealth
    // и др. не крашились при проверке u.isSleeping(...) если случайно
    // попадут на Hero.
    isSleeping: function () { return false; },
    isStatModified: function (name: string) {
      return this.current?.[name] !== this.base?.[name];
    },
  };
}

// ПвП.fix-influence: MapSchema<number> → Record<string, number>.
// Колизей-обёртки имеют forEach((value, key)) сигнатуру.
function mapToCounts(m: any): Record<string, number> {
  const out: Record<string, number> = {};
  if (!m) return out;
  if (typeof m.forEach === 'function') {
    m.forEach((v: number, k: string) => { out[k] = v; });
  } else if (typeof m === 'object') {
    for (const k of Object.keys(m)) out[k] = Number(m[k]) || 0;
  }
  return out;
}

function adaptPlayer(p: any): any {
  if (!p) {
    return null;
  }
  const line0 = (p.line0 || []).map(adaptUnit);
  const line1 = (p.line1 || []).map(adaptUnit);
  // Гарантируем длину 4 (для match.players[i].field[0][3] доступ).
  while (line0.length < 4) line0.push(null);
  while (line1.length < 4) line1.push(null);

  const hero = adaptHero(p.hero);

  // ПвП.6.2: восстанавливаем pendingAction если сервер прислал instanceId.
  const pendingAction = (p.pendingActionInstanceId && p.pendingActionInstanceId !== 0)
    ? { cardInstanceId: p.pendingActionInstanceId, targetInstanceId: null, opts: {} }
    : null;

  return {
    sessionId: p.sessionId,
    playerId: p.playerId || 0,    // ПвП.5b: для finishMatch.opponentId
    name: p.nick || '',
    nick: p.nick || '',
    faction: p.faction || '',
    // Этап 68.3: публичная косметика (для показа сопернику в конце матча).
    accentColor: p.accentColor || null,
    avatarId: p.avatarId || null,
    titleCode: p.titleCode || null,
    titleText: p.titleText || null,
    hero,
    field: [line0, line1],
    hand: (p.hand || []).map(adaptHandCard).filter(Boolean),
    discard: (p.discard || []).map(adaptHandCard).filter(Boolean),
    // deck-viewer: свой состав колоды приходит через приватный @view (deckView,
    // отсортирован по id — без порядка вытягивания). У соперника deckView пуст
    // (под @view) → null-заглушка нужной длины (содержимое скрыто).
    deck: (p.deckView && p.deckView.length > 0)
      ? p.deckView.map(adaptHandCard).filter(Boolean)
      : new Array(p.deckSize || 0).fill(null),
    scripts: p.scripts || 0,
    influence: p.influence || 0,
    firewalls: p.firewalls || 0,
    heroBonusAttackPending: p.heroBonusAttackPending || 0,
    extraPlaysNextTurn: 0,
    pendingAction,
    // ПвП.fix-influence: Стенд Влияния Деуса. influenceOffer — ArraySchema<string>,
    // на JS-уровне ведёт себя как массив (есть forEach/map). influencePlayCounts —
    // MapSchema<number>, конвертируем в plain Record.
    influenceOffer: Array.from(p.influenceOffer || []) as string[],
    influencePlayCounts: mapToCounts(p.influencePlayCounts),
    // End-turn indicator: игрок подтвердил «ход» и ждёт второго.
    // BattleScreen рисует баннер когда enemy.endTurnAcked && !me.endTurnAcked.
    endTurnAcked: !!p.endTurnAcked,
    heroActiveUsed: !!p.hero?.heroActiveUsed,
    heroActiveUsedThisMatch: !!p.hero?.heroActiveUsed,
    unitsOnField: function () {
      const out: any[] = [];
      for (const row of this.field) {
        for (const u of row) if (u && u.alive) out.push(u);
      }
      return out;
    },
  };
}

// ПвП.6.2: проносит pendingPlacements из snap.players[i] (массив HandCard'ов +
// параллельные line/slot/target массивы) в формат match.pendingPlacements[i][k] =
// { cardInstanceId, line, slot, targetInstanceId }.
function adaptPendingPlacements(p: any): any[] {
  if (!p) return [];
  const cards = p.pendingPlacements || [];
  const lines = p.pendingPlacementsLines || [];
  const slots = p.pendingPlacementsSlots || [];
  const targets = p.pendingPlacementsTargets || [];
  const out: any[] = [];
  for (let i = 0; i < cards.length; i++) {
    const c = cards[i];
    out.push({
      cardInstanceId: c.instanceId,
      line: lines[i] >= 0 ? lines[i] : undefined,
      slot: slots[i] >= 0 ? slots[i] : undefined,
      targetInstanceId: targets[i] > 0 ? targets[i] : undefined,
    });
  }
  return out;
}

// Главная функция. mySessionId — наш sessionId в комнате, нужен чтобы
// поставить «меня» в players[0] на клиенте.
export function colyseusToMatch(snap: ColyseusSnap, mySessionId: string): any {
  // Находим себя в массиве серверных игроков.
  let myIdx: 0 | 1 = 0;
  for (let i = 0; i < snap.players.length; i++) {
    if (snap.players[i]?.sessionId === mySessionId) {
      myIdx = i as 0 | 1; break;
    }
  }

  // На клиенте всегда [me, enemy]. Свапаем массив если на сервере я под idx=1.
  const players: any[] = [];
  if (myIdx === 1) {
    players[0] = adaptPlayer(snap.players[1]);
    players[1] = adaptPlayer(snap.players[0]);
  } else {
    players[0] = adaptPlayer(snap.players[0]);
    players[1] = adaptPlayer(snap.players[1]);
  }

  // ПвП.8-fix-hero-rect: Hero не имеет instanceId в engine — даём
  // синтетический стабильный ID чтобы BattleAnimator.cardEl находил
  // DOM-элемент через [data-instance-id]. Моя сторона -1, вражеская -2.
  if (players[0]?.hero) players[0].hero.instanceId = -1;
  if (players[1]?.hero) players[1].hero.instanceId = -2;

  // firstPlayerIdx с точки зрения клиента: если на сервере first был наш сосед,
  // на клиенте first = 1.
  const firstPlayerIdx: 0 | 1 = (myIdx === 1)
    ? ((snap.firstPlayerIdx === 0 ? 1 : 0) as 0 | 1)
    : (snap.firstPlayerIdx as 0 | 1);

  // winnerIdx с точки зрения клиента.
  let winnerIdx: 0 | 1 | 'draw' | undefined;
  if (snap.matchOver) {
    if (!snap.winnerSessionId) winnerIdx = 'draw';
    else if (snap.winnerSessionId === mySessionId) winnerIdx = 0;
    else winnerIdx = 1;
  }

  return {
    _pvp: true,             // маркер для engine-shim — роутить действия в room.send
    _pvpMyIdx: myIdx,       // оригинальный индекс на сервере (для send)
    phase: snap.phase,
    currentTurn: snap.currentTurn,
    firstPlayerIdx,
    matchOver: snap.matchOver,
    winnerIdx,
    log: {
      entries: snap.recentLog || [],
      // onAdd-подписки в PvP не работают (всё приходит через onStateChange).
      // Возвращаем noop unsubscribe.
      onAdd: function (_cb: (entry: string) => void) { return () => {}; },
    },
    rng: () => Math.random(),
    players,
    // ПвП.6.2: pendingPlacements теперь синкаются (нужно для preview юнитов
    // и хайлайта pending в руке). Сохраняем перспективу клиента: index 0 = я.
    pendingPlacements: (myIdx === 1)
      ? [adaptPendingPlacements(snap.players[1]), adaptPendingPlacements(snap.players[0])]
      : [adaptPendingPlacements(snap.players[0]), adaptPendingPlacements(snap.players[1])],
    pendingMods: [[], []], // отдельно пока не синкаются (модов хвост позже).
    // ТАЙ.1: deadline текущей фазы. 0 → null (нет таймера).
    phaseDeadlineAt: snap.phaseDeadlineMs && snap.phaseDeadlineMs > 0
      ? snap.phaseDeadlineMs
      : null,
    // ВОЙ.1: war-режим. mySessionId нужен чтобы сравнить с
    // warSnapPendingBy — определить кто инициатор (показать widget'у
    // моё ожидание или модалку врагу).
    warStakeUsd: snap.warStakeUsd || 0,
    warMultiplier: snap.warMultiplier || 1,
    warSnapPendingBy: snap.warSnapPendingBy || '',
    warSnapPendingIsMine: !!snap.warSnapPendingBy
      && snap.warSnapPendingBy === mySessionId,
    warSnapDeadlineAt: snap.warSnapDeadlineMs && snap.warSnapDeadlineMs > 0
      ? snap.warSnapDeadlineMs
      : null,
    warEndReason: snap.warEndReason || '',
  };
}

// Пустой Match-шелл для первичной инициализации useState — до прихода
// первого state-update от сервера. Чтобы render не падал на null.players.
export function makeEmptyPvpMatch(): any {
  return {
    _pvp: true,
    phase: 'waiting',
    currentTurn: 1,
    firstPlayerIdx: 0,
    matchOver: false,
    log: { entries: [], onAdd: () => () => {} },
    rng: () => Math.random(),
    players: [
      makeEmptyPlayer(),
      makeEmptyPlayer(),
    ],
    pendingPlacements: [[], []],
    pendingMods: [[], []],
  };
}

function makeEmptyPlayer(): any {
  const emptyLine = [null, null, null, null];
  return {
    sessionId: '',
    name: '—', nick: '—', faction: '',
    hero: {
      id: '', name_ru: '—', faction: '', type: 'hero',
      base: { atk: 0, armor: 0, hp: 1 },
      current: { atk: 0, armor: 0, hp: 1, keywords: [], silenced: false },
      atk: 0, armor: 0, hp: 1, keywords: [],
      silenced: false, silenceSources: new Set(),
      heroActiveUsed: false, heroActiveUsedThisMatch: false,
      heroAttackedThisTurn: false, alive: true,
      isAlive: function () { return true; },
    },
    field: [emptyLine, emptyLine],
    hand: [], discard: [],
    deck: [],
    scripts: 0, influence: 0, firewalls: 0,
    heroBonusAttackPending: 0,
    extraPlaysNextTurn: 0,
    pendingAction: null,
    influenceOffer: [] as string[], influencePlayCounts: {} as Record<string, number>,
    endTurnAcked: false,
    heroActiveUsed: false, heroActiveUsedThisMatch: false,
    unitsOnField: function () { return []; },
  };
}
